/**
 * Scheda della segnalazione: barra del processo in 4 passi (segnalata, presa in carico, azioni in corso, chiusa), cosa è
 * successo, azioni ISO 9001, allegati, tipo e cause (proposti o approvati), NC collegate, aggiornamenti; a destra dati,
 * tempi PRO 09 e claim collegato (QA26-F4.9..F4.11). La segnalazione non è una NC: la NC si apre a mano, se serve.
 */
import { App, Button, Card, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Switch, Table, Tag } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ANCORA } from "@qa/shared";
import {
  FONTE_IMPORT,
  SEVERITA,
  STATI_SEGNALAZIONE,
  VALUTE,
  type ContenutoClassificazioneT,
  type EsitoDecisioniDto,
  type ModificaSegnalazioneInput,
  type NcRigaDto,
  type PropostaRigaDto,
  type SegnalazioneDto,
} from "@qa/shared";
import { AzioniCard } from "./scheda/Azioni";
import { AllegatiCard } from "./scheda/Allegati";
import { ApriNcModal, Pro09Card } from "./scheda/Pro09";
import { api } from "../../api/client";
import { fmtData, fmtEur, fmtImporto, numFormatter, numParser } from "../../lib/format";
import { usePuo } from "../../lib/permessi";
import { useAggiorna } from "../../lib/query";
import { messaggioErrore } from "../../i18n/errori";
import { FornitoreSelect, OwnerInput, SelectLista } from "../../components/campi";
import { Flag, Miss, Owner, Storico, PageHead, Processo, Prop, Ro, Sev, Stato, StatoClaimTag, StatoRf, TipoNcTag, Urgente, Vuoto } from "../../components/ui";
import { palette } from "../../theme";

function Classificazione({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const puo = usePuo();
  const aggiorna = useAggiorna();
  const admin = puo("proposta.decidi");
  const vedeFornitori = puo("fornitore.vedi");
  const pendente = r.classificazioneProposta;
  const base = pendente ?? r.classificazione;
  const iniziale: ContenutoClassificazioneT = useMemo(
    () => ({
      tipoSegnalazione: base?.tipoSegnalazione ?? r.tipoSegnalazione ?? null,
      tipi: base?.tipi ?? [],
      causaCliente: base?.causaCliente ?? null,
      causaChi: base?.causaChi ?? null,
      fornitoreId: base?.fornitore?.id ?? null,
      fornitoreTesto: base?.fornitoreTesto ?? null,
    }),
    [base, r.tipoSegnalazione],
  );
  const [c, setC] = useState<ContenutoClassificazioneT>(iniziale);
  useEffect(() => setC(iniziale), [iniziale]);
  const cambiata = JSON.stringify(c) !== JSON.stringify(iniziale);
  const contenuto = (): ContenutoClassificazioneT => c;

  const esito = (e: EsitoDecisioniDto) => {
    if (e.errori.length) message.error(e.errori.map((x) => x.messaggio).join(" · "));
    else message.success(t("redflag.scheda.approvataOk"));
  };
  const m = useMutation({
    mutationFn: async (az: "approva" | "modifica" | "rifiuta" | "proponi" | "approvaNuova") => {
      if (az === "proponi") return api.post<PropostaRigaDto>("/proposte", { tipo: "classificazione", segnalazioneId: r.id, contenuto: contenuto() });
      if (az === "approvaNuova") {
        const p = await api.post<PropostaRigaDto>("/proposte", { tipo: "classificazione", segnalazioneId: r.id, contenuto: contenuto() });
        return api.post<EsitoDecisioniDto>(`/proposte/${p.id}/approva`, {});
      }
      const id = pendente!.proposta.id;
      if (az === "approva") return api.post<EsitoDecisioniDto>(`/proposte/${id}/approva`, {});
      if (az === "modifica") return api.post<EsitoDecisioniDto>(`/proposte/${id}/modifica`, { contenuto: contenuto() });
      return api.post<PropostaRigaDto>(`/proposte/${id}/rifiuta`, {});
    },
    onSuccess: (res, az) => {
      if (az === "proponi") message.success(t("redflag.scheda.propostaInviata"));
      else if (az === "rifiuta") message.success(t("redflag.scheda.rifiutataOk"));
      else esito(res as EsitoDecisioniDto);
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const vai = (az: Parameters<typeof m.mutate>[0]) => {
    if (az !== "rifiuta" && !c.tipoSegnalazione) return void message.warning(t("redflag.scheda.tipiObbligatori"));
    if (az === "rifiuta") return void modal.confirm({ title: t("proposte.rifiutaTitolo", { codice: pendente?.proposta.codice ?? "" }), okButtonProps: { danger: true }, okText: t("redflag.scheda.rifiuta"), cancelText: t("azioni.annulla"), onOk: () => m.mutateAsync(az) });
    m.mutate(az);
  };

  return (
    <Card
      size="small"
      data-tip={ANCORA.classifica}
      style={{ marginTop: 16 }}
      title={
        <Space wrap size={4}>
          {t("redflag.scheda.classificazione")}
          {pendente ? <Prop>{t("redflag.scheda.propostaInAttesa", { codice: pendente.proposta.codice })}</Prop> : r.classificazione ? <Ro>{t("redflag.scheda.approvata")}</Ro> : null}
        </Space>
      }
    >
      <p className="muted" style={{ margin: "0 0 10px" }}>
        {!base ? t("redflag.scheda.nessunaClassificazione") : t("redflag.scheda.classificazioneNota")}
        {pendente && <span className="small"> · {t("redflag.scheda.propostaDa", { chi: pendente.proposta.propostaDa })}</span>}
      </p>
      <Row gutter={[10, 10]}>
        <Col xs={24} md={6}>
          <div className="muted small">{t("redflag.scheda.tipoSegnalazione")}</div>
          <SelectLista lista="tipo_segnalazione" value={c.tipoSegnalazione} onChange={(v) => setC({ ...c, tipoSegnalazione: v })} />
          {base && base.tipi.length > 0 && (
            <div className="muted small" style={{ marginTop: 2 }}>
              {base.tipi.map((x) => t(`dominio.tipoClass.${x}`)).join(" + ")}
            </div>
          )}
        </Col>
        <Col xs={24} md={6}>
          <div className="muted small">{t("redflag.scheda.causaCliente")}</div>
          <SelectLista lista="causa_cliente" value={c.causaCliente} onChange={(v) => setC({ ...c, causaCliente: v })} />
        </Col>
        <Col xs={24} md={6}>
          <div className="muted small">{t("redflag.scheda.causaChi")}</div>
          <SelectLista lista="causa_chi" value={c.causaChi} onChange={(v) => setC({ ...c, causaChi: v })} />
        </Col>
        <Col xs={24} md={6}>
          <div className="muted small">{t("redflag.scheda.fornitore")}</div>
          {vedeFornitori ? (
            <FornitoreSelect value={c.fornitoreId} iniziale={base?.fornitore} onChange={(id) => setC({ ...c, fornitoreId: id })} />
          ) : (
            <Input value={c.fornitoreTesto ?? ""} onChange={(e) => setC({ ...c, fornitoreTesto: e.target.value || null })} />
          )}
          {base?.fornitoreTesto && !base.fornitore && <div className="muted small">{t("redflag.scheda.fornitoreFile", { nome: base.fornitoreTesto })}</div>}
        </Col>
      </Row>
      <Space wrap style={{ marginTop: 12 }}>
        {admin ? (
          pendente ? (
            <>
              <Button type="primary" loading={m.isPending} disabled={cambiata} onClick={() => vai("approva")}>
                {t("redflag.scheda.approvaApri")}
              </Button>
              <Button loading={m.isPending} disabled={!cambiata} onClick={() => vai("modifica")}>
                {t("redflag.scheda.modificaApprova")}
              </Button>
              <Button danger loading={m.isPending} onClick={() => vai("rifiuta")}>
                {t("redflag.scheda.rifiuta")}
              </Button>
            </>
          ) : (
            <Button type="primary" loading={m.isPending} disabled={!cambiata} onClick={() => vai("approvaNuova")}>
              {r.classificazione ? t("redflag.scheda.modificaApprova") : t("redflag.scheda.approvaApri")}
            </Button>
          )
        ) : (
          puo("proposta.crea") && (
            <>
              <Button type="primary" loading={m.isPending} disabled={!cambiata} onClick={() => vai("proponi")}>
                {t("redflag.scheda.proponi")}
              </Button>
              <span className="muted small">{t("redflag.scheda.proponiNota")}</span>
            </>
          )
        )}
      </Space>
    </Card>
  );
}

function NcCollegate({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  const [apri, setApri] = useState(false);
  const tipo = r.tipoSegnalazione ?? r.classificazioneProposta?.tipoSegnalazione;
  return (
    <Card
      size="small"
      title={t("redflag.scheda.ncTitolo")}
      style={{ marginTop: 16 }}
      extra={
        r.puo.aprireNc &&
        r.nc.length < 2 && (
          <Button size="small" onClick={() => setApri(true)}>
            {t("redflag.apriNc.bottone")}
          </Button>
        )
      }
    >
      {apri && <ApriNcModal r={r} onClose={() => setApri(false)} />}
      {r.nc.length ? (
        <Table<NcRigaDto>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={r.nc}
          scroll={{ x: 640 }}
          columns={[
            { title: t("redflag.scheda.ncCol"), key: "c", render: (_, n) => <Link className="mono" to={`/redflag/nc/${n.id}`}>{n.codice}</Link> },
            { title: t("redflag.scheda.ncTipo"), key: "t", render: (_, n) => <TipoNcTag tipo={n.tipo} /> },
            {
              title: t("redflag.scheda.ncFornitore"),
              key: "f",
              render: (_, n) =>
                n.fornitore ? <Link to={`/fornitori/${n.fornitore.id}`}>{n.fornitore.nome}</Link> : n.funzione ?? n.fornitoreTesto ?? <Miss>{n.tipo === "fornitore" ? t("comune.daIndividuare") : t("comune.daAssegnare")}</Miss>,
            },
            { title: t("redflag.scheda.ncFase"), key: "fa", render: (_, n) => <span className="mono">{n.stato === "chiusa" ? t("nc.chiusa") : n.fase}</span> },
            { title: t("redflag.scheda.ncOwner"), key: "o", render: (_, n) => <Owner o={n.owner} /> },
            { title: t("redflag.scheda.ncScadenza"), key: "s", render: (_, n) => <span className={n.scaduta ? "miss" : "num"}>{fmtData(n.scadenza)}</span> },
          ]}
        />
      ) : (
        <Vuoto titolo={t("redflag.scheda.ncVuotaTitolo")} testo={tipo === "Gara persa" ? t("redflag.scheda.ncVuotaCommerciale") : t("redflag.scheda.ncVuotaAttesa")} />
      )}
    </Card>
  );
}

function Aggiornamenti({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  return (
    <Card size="small" title={t("redflag.scheda.aggiornamenti")} style={{ marginTop: 16 }} data-tip={ANCORA.aggiornamenti}>
      <Storico righe={r.aggiornamenti} vuoto={t("redflag.scheda.nessunAggiornamento")} />
    </Card>
  );
}

type FormDati = Pick<
  ModificaSegnalazioneInput,
  | "societa"
  | "cliente"
  | "progetto"
  | "severita"
  | "urgente"
  | "valoreImporto"
  | "valuta"
  | "stato"
  | "casoSalesforce"
  | "poSap"
  | "ownerQualita"
  | "ownerCommerciale"
  | "fonte"
  | "fonteDettaglio"
  | "tipoSegnalazione"
  | "problema"
  | "impatto"
  | "verificataConCliente"
>;

function ModificaDati({ r, open, onClose }: { r: SegnalazioneDto; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [form] = Form.useForm<FormDati>();
  const m = useMutation({
    mutationFn: (b: FormDati) => api.patch<SegnalazioneDto>(`/redflag/segnalazioni/${r.id}`, b),
    onSuccess: () => {
      message.success(t("comune.salvato"));
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Modal open={open} onCancel={onClose} title={t("redflag.scheda.modificaDati")} okText={t("azioni.salva")} cancelText={t("azioni.annulla")} confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnHidden width={640}>
      <Form<FormDati>
        form={form}
        layout="vertical"
        initialValues={{
          societa: r.societa,
          cliente: r.cliente,
          progetto: r.progetto,
          severita: r.severita,
          urgente: r.urgente,
          valoreImporto: r.valoreImporto,
          valuta: r.valuta ?? "EUR",
          stato: r.stato,
          casoSalesforce: r.casoSalesforce,
          poSap: r.poSap,
          ownerQualita: r.ownerQualita,
          ownerCommerciale: r.ownerCommerciale,
          fonte: r.fonte ?? undefined,
          fonteDettaglio: r.fonteDettaglio,
          tipoSegnalazione: r.tipoSegnalazione,
          problema: r.problema,
          impatto: r.impatto,
          verificataConCliente: r.verificataConCliente,
        }}
        onFinish={(v) =>
          m.mutate({
            ...v,
            progetto: v.progetto || null,
            casoSalesforce: v.casoSalesforce || null,
            poSap: v.poSap || null,
            fonteDettaglio: v.fonteDettaglio || null,
            problema: v.problema || null,
            impatto: v.impatto || null,
            ...(v.stato === "chiusa" ? {} : { verificataConCliente: undefined }),
          })
        }
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="fonte" label={t("redflag.scheda.fonte")} rules={[{ required: true, message: t("redflag.nuova.obbligatorio") }]}>
              <SelectLista lista="fonte" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="tipoSegnalazione" label={t("redflag.scheda.tipoSegnalazione")}>
              <SelectLista lista="tipo_segnalazione" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="fonteDettaglio" label={t("redflag.scheda.fonteDettaglio")}>
          <Input maxLength={300} placeholder={t("redflag.nuova.fonteDettaglioPh")} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="societa" label={t("redflag.scheda.societa")} rules={[{ required: true, message: t("redflag.nuova.obbligatorio") }]}>
              <SelectLista lista="societa" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="cliente" label={t("redflag.scheda.cliente")} rules={[{ required: true, message: t("redflag.nuova.obbligatorio") }]}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="progetto" label={t("redflag.scheda.progetto")}>
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="problema" label={t("redflag.scheda.problema")}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} maxLength={5000} />
        </Form.Item>
        <Form.Item name="impatto" label={t("redflag.scheda.impatto")}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} maxLength={5000} placeholder={t("redflag.nuova.impattoPh")} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="severita" label={t("redflag.scheda.severita")}>
              <Select options={SEVERITA.map((s) => ({ value: s, label: s }))} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="stato" label={t("redflag.scheda.stato")}>
              <Select options={STATI_SEGNALAZIONE.map((s) => ({ value: s, label: t(`dominio.statoRf.${s}`) }))} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="urgente" label={t("redflag.scheda.urgente")} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item noStyle shouldUpdate={(a, b) => a.stato !== b.stato}>
          {({ getFieldValue }) =>
            getFieldValue("stato") === "chiusa" && (
              <Form.Item name="verificataConCliente" valuePropName="checked" rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error(t("errori.chiusura_verifica_cliente")))) }]}>
                <Checkbox>{t("redflag.scheda.verificataConCliente")}</Checkbox>
              </Form.Item>
            )
          }
        </Form.Item>
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item name="valoreImporto" label={t("redflag.scheda.valore")}>
              <InputNumber min={0} style={{ width: "100%" }} formatter={numFormatter} parser={numParser} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="valuta" label={t("redflag.nuova.valuta")}>
              <Select options={VALUTE.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="casoSalesforce" label={t("redflag.scheda.casoSf")}>
              <Input maxLength={40} placeholder={t("redflag.scheda.casoSfEsempio")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="poSap" label={t("redflag.scheda.poSap")}>
              <Input maxLength={40} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="ownerCommerciale" label={t("redflag.scheda.ownerCommerciale")}>
              <OwnerInput />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="ownerQualita" label={t("redflag.scheda.ownerQualita")}>
              <OwnerInput />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function Dati({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  const puo = usePuo();
  const [open, setOpen] = useState(false);
  return (
    <Card
      size="small"
      title={t("redflag.scheda.dati")}
      extra={
        puo("rf.modifica") && (
          <Button size="small" onClick={() => setOpen(true)}>
            {t("azioni.modifica")}
          </Button>
        )
      }
    >
      <dl className="kv stretto">
        <dt>{t("redflag.scheda.societa")}</dt>
        <dd>{r.societa}</dd>
        <dt>{t("redflag.scheda.cliente")}</dt>
        <dd>
          {r.cliente}{" "}
          {r.vip && (
            <Tag color="gold" bordered={false}>
              {t("redflag.vip")}
            </Tag>
          )}
        </dd>
        <dt>{t("redflag.scheda.fonte")}</dt>
        <dd>
          {r.fonte ?? <Miss>{t("comune.daCompilare")}</Miss>}
          {r.fonteDettaglio && <div className="muted small">{r.fonteDettaglio}</div>}
        </dd>
        <dt>{t("redflag.scheda.tipoSegnalazione")}</dt>
        <dd>{r.tipoSegnalazione ?? (r.classificazioneProposta?.tipoSegnalazione ? <Prop>{r.classificazioneProposta.tipoSegnalazione}</Prop> : <Miss>{t("comune.daCompilare")}</Miss>)}</dd>
        <dt>{t("redflag.scheda.valore")}</dt>
        <dd className="num" data-sensitive>
          {r.valoreTesto ?? fmtImporto(r.valoreImporto, r.valuta ?? "EUR")}
          {r.valoreEur !== null && r.valuta !== "EUR" && <small className="muted"> ({fmtEur(r.valoreEur)})</small>}
        </dd>
        {puo("costi.vedi") && (
          <>
            <dt>{t("redflag.scheda.costo")}</dt>
            <dd>{r.costoNonQualita ? <span className="num" data-sensitive>{fmtEur(r.costoNonQualita)}</span> : <Miss>{t("comune.daCompilare")}</Miss>}</dd>
          </>
        )}
        <dt>{t("redflag.scheda.casoSf")}</dt>
        <dd className="mono">{r.casoSalesforce ?? "—"}</dd>
        <dt>{t("redflag.scheda.poSap")}</dt>
        <dd className="mono">{r.poSap ?? "—"}</dd>
        <dt>{t("redflag.scheda.ownerCommerciale")}</dt>
        <dd>
          <Owner o={r.ownerCommerciale} />
        </dd>
        <dt>{t("redflag.scheda.ownerQualita")}</dt>
        <dd>
          <Owner o={r.ownerQualita} />
        </dd>
        {r.creataDa && (
          <>
            <dt>{t("redflag.scheda.creataDa")}</dt>
            <dd>{r.creataDa}</dd>
          </>
        )}
      </dl>
      {open && <ModificaDati r={r} open={open} onClose={() => setOpen(false)} />}
    </Card>
  );
}

function ClaimCollegato({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  const puo = usePuo();
  const proposti = r.proposte.filter((p) => p.tipo === "collegamento_claim" && p.stato === "da_approvare");
  if (!puo("claim.vedi") || (!r.claim.length && !proposti.length)) return null;
  return (
    <Card size="small" title={t("redflag.scheda.claimTitolo")} style={{ marginTop: 16, borderColor: palette.warn }}>
      {r.claim.map((c) => (
        <div key={c.id} style={{ marginBottom: 10 }}>
          <p style={{ margin: "0 0 4px" }}>
            <Link className="mono" to={`/claim/${c.id}`}>
              {c.codice}
            </Link>
            {" · "}
            {c.fornitore && puo("fornitore.vedi") ? <Link to={`/fornitori/${c.fornitore.id}`}>{c.fornitore.nome}</Link> : c.fornitoreTesto}
          </p>
          <p className="num" data-sensitive style={{ fontSize: 20, fontWeight: 700, margin: "4px 0" }}>
            {fmtImporto(c.importo, c.valuta)}
          </p>
          <p style={{ margin: 0 }} className="muted">
            {c.risoluzione} · <StatoClaimTag s={c.stato} />
          </p>
        </div>
      ))}
      {proposti.map((p) => (
        <p key={p.id} className="small" style={{ margin: "6px 0 0" }}>
          {p.claimId ? (
            <Link className="mono" to={`/claim/${p.claimId}`}>
              {p.oggetto}
            </Link>
          ) : (
            <span className="mono">{p.oggetto}</span>
          )}{" "}
          <Prop>{t("comune.proposto")}</Prop>
          <br />
          <span className="muted">{t("redflag.scheda.claimProposto", { codice: p.codice })}</span>
        </p>
      ))}
    </Card>
  );
}

function NuovoAggiornamento({ r, open, onClose }: { r: SegnalazioneDto; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [testo, setTesto] = useState("");
  const m = useMutation({
    mutationFn: () => api.post<SegnalazioneDto>(`/redflag/segnalazioni/${r.id}/aggiornamenti`, { testo: testo.trim() }),
    onSuccess: () => {
      message.success(t("comune.salvato"));
      setTesto("");
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Modal open={open} onCancel={onClose} title={t("redflag.scheda.aggiungiAggiornamento")} okText={t("azioni.salva")} cancelText={t("azioni.annulla")} okButtonProps={{ disabled: !testo.trim() }} confirmLoading={m.isPending} onOk={() => m.mutate()}>
      <Input.TextArea rows={4} maxLength={4000} placeholder={t("redflag.scheda.testoAggiornamento")} value={testo} onChange={(e) => setTesto(e.target.value)} />
    </Modal>
  );
}

export default function SegnalazionePage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const puo = usePuo();
  const [agg, setAgg] = useState(false);
  const q = useQuery({ queryKey: ["segnalazione", id], queryFn: () => api.get<SegnalazioneDto>(`/redflag/segnalazioni/${id}`) });
  return (
    <Stato q={q} righe={12}>
      {(r) => {
        const passo = r.stato === "chiusa" ? 5 : r.passo;
        const st = (n: number) => (n < passo ? "done" : n === passo ? "cur" : "") as "done" | "cur" | "";
        return (
          <>
            <PageHead
              crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoRedflag"), to: "/redflag" }, { label: t("menu.segnalazioni"), to: "/redflag/segnalazioni" }, { label: r.codice }]}
              title={
                <>
                  {r.cliente}
                  {r.progetto ? ` · ${r.progetto}` : ""} <Flag f={r.flag} />
                </>
              }
              sub={t("redflag.scheda.sottotitolo", { codice: r.codice, societa: r.societa, data: r.dataTesto ?? fmtData(r.dataSegnalazione) })}
              extra={
                <>
                  <Sev s={r.severita} />
                  {r.urgente && <Urgente lungo />}
                  <StatoRf s={r.stato} />
                  {puo("rf.modifica") && <Button onClick={() => setAgg(true)}>{t("redflag.scheda.aggiungiAggiornamento")}</Button>}
                </>
              }
            />
            <Processo
              passi={[
                { titolo: t("redflag.processo.p1"), chi: t("redflag.processo.p1r"), stato: st(1) },
                { titolo: t("redflag.processo.p2"), chi: t("redflag.processo.p2r"), stato: st(2) },
                { titolo: t("redflag.processo.p3"), chi: t("redflag.processo.p3r"), stato: st(3) },
                { titolo: t("redflag.processo.p4"), chi: t("redflag.processo.p4r"), stato: st(4) },
              ]}
            />
            <Row gutter={[16, 16]}>
              <Col xs={24} xl={16}>
                <Card size="small" title={t("redflag.scheda.cosa")}>
                  <dl className="kv">
                    <dt>{t("redflag.scheda.problema")}</dt>
                    <dd style={{ whiteSpace: "pre-wrap" }}>{r.problema ?? "—"}</dd>
                    <dt>{t("redflag.scheda.causa")}</dt>
                    <dd style={{ whiteSpace: "pre-wrap" }}>{r.causa ?? "—"}</dd>
                    <dt>{t("redflag.scheda.impatto")}</dt>
                    <dd style={{ whiteSpace: "pre-wrap" }}>{r.impatto ?? "—"}</dd>
                    <dt>{t("redflag.scheda.azioneInCorso")}</dt>
                    <dd style={{ whiteSpace: "pre-wrap" }}>{r.azioneInCorso ?? "—"}</dd>
                  </dl>
                  {r.fonte === FONTE_IMPORT && (
                    <p className="muted small" style={{ margin: "10px 0 0" }}>
                    {t("redflag.scheda.notaLingua")}
                  </p>
                  )}
                </Card>
                <AzioniCard r={r} />
                <AllegatiCard r={r} />
                <Classificazione r={r} />
                <NcCollegate r={r} />
                <Aggiornamenti r={r} />
              </Col>
              <Col xs={24} xl={8}>
                <Dati r={r} />
                <Pro09Card r={r} />
                <ClaimCollegato r={r} />
              </Col>
            </Row>
            {agg && <NuovoAggiornamento r={r} open={agg} onClose={() => setAgg(false)} />}
          </>
        );
      }}
    </Stato>
  );
}
